import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setNotification } from "../../redux/slices/notificationSlice";
import { setInquiryDetails } from "../../redux/slices/inquirySlice";
import { setPaymentData as setPaymentDataAction, setPaymentToken } from "../../redux/slices/paymentSlice";
import { messaging, onMessage } from "../../config/firebaseConfig";
import PaymentInstructionVA from "../components/PaymentInstructionVA";
import PaymentInstructionOffline from "../components/PaymentInstructionOffline";
import PaymentInstructionQRIS from "../components/PaymentInstructionQRIS";
import PaymentInstructionEwallet from "../components/PaymentInstructionEwallet";
import PaymentInstructionCard from "../components/PaymentInstructionCard";
import HostedCardCheckout from "../components/HostedCardCheckout";
import { registerServiceWorker } from "../../config/firebaseConfig";
import { getPaymentLinkHeaders } from "../../utils/paymentLinkHeaders"; // Import secure headers utility
import { authorizedFetch, SessionExpiredError } from "../../utils/authorizedFetch";
import {
  handlePaymentLinkError,
  createErrorNavigationState,
  PaymentLinkErrorCode,
  getErrorTitle,
  getErrorMessage,
} from "../../utils/paymentLinkErrorHandler";
import { getEarliestExpiration } from "../../utils/paymentLinkExpiration";
import { resolveEwalletInstruction } from "../utils/ewalletFlowRouter";
import { getExpiredRedirectUrl, getSuccessRedirectUrl, redirectToMerchantUrl } from "../utils/paymentLinkRedirects";
import { areNotificationsGranted } from "../utils/notificationPermission";
import useTranslation from "../hooks/useTranslation";

const BASE_URL = process.env.REACT_APP_API_URL;
// How often to re-check payment status when the browser can't deliver push notifications
// (permission denied/never asked) — the fallback for the FCM onMessage auto-redirect.
const STATUS_POLL_INTERVAL_MS = 5000;

const ThirdForm = () => {
  // untuk terima notif di background firebase
  registerServiceWorker();

  const location = useLocation();
  const navigate = useNavigate();
  const { reffNo } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const paymentLinkToken = useSelector((state) => state.payment.token);
  // const [loading, setLoading] = useState(false);

  // ✅ Deklarasikan state yang dibutuhkan sebelum hooks lain
  const [historyId, setHistoryId] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  // ✅ Fungsi untuk fetch data dari API berdasarkan reffNo
  const fetchPaymentData = useCallback(
    async (reffNoParam) => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}/api/payment-link/get-by-reff`, {
          method: "POST",
          headers: getPaymentLinkHeaders(),
          body: JSON.stringify({ reff_no: reffNoParam }),
        });

        if (!response.ok) {
          // Handle error with proper error codes
          const errorInfo = await handlePaymentLinkError(response);

          // Extract payment data from error response (if available)
          const paymentData = errorInfo.paymentData || {};

          // Navigate to appropriate error page based on error code
          if (errorInfo.isExpired) {
            const expiredRedirectUrl = getExpiredRedirectUrl(paymentData);

            if (redirectToMerchantUrl(expiredRedirectUrl)) {
              return;
            }

            navigate("/payment-expired", {
              replace: true,
              state: {
                ...createErrorNavigationState(errorInfo, {
                  reffNo: paymentData.reff_no || reffNoParam,
                }),
                selectedMethod: paymentData.payment_method_name || "-",
                amount: paymentData.amount || 0,
                redirectUrl: expiredRedirectUrl,
              },
            });
          } else if (errorInfo.isPaid) {
            const successRedirectUrl = getSuccessRedirectUrl(paymentData);

            if (redirectToMerchantUrl(successRedirectUrl)) {
              return;
            }

            navigate("/payment-success", {
              replace: true,
              state: {
                reffNo: paymentData.reff_no || reffNoParam,
                amount: paymentData.amount || 0,
                customerName: paymentData.customer_name || null,
                email: paymentData.customer_email || null,
                paymentMethod: paymentData.payment_method_name || "-",
                paymentDate: paymentData.payment_date || "-",
                redirectUrl: successRedirectUrl,
              },
            });
          } else {
            // For other errors (not found, failed, etc.), navigate to generic error
            navigate("/wrong-page");
          }
          return;
        }

        const data = await response.json();
        // ✅ Struktur response dari get-by-reff sudah sama dengan proceed-inquiry
        // Response: { success, code, data: { paymentLink, paymentLinkHistory, paymentMethod } }
        setPaymentData({
          amount: data.data.paymentLinkHistory.amount,
          orderId: data.data.paymentLinkHistory.reff_no,
          result: data, // ✅ Simpan seluruh response object, sama seperti di SecondForm
        });

        dispatch(setPaymentDataAction(data.data.paymentLink));
        // get-by-reff issues a fresh payment-link-scoped token (needed here since this
        // path runs on a direct page load, where any previous in-memory token is gone).
        dispatch(setPaymentToken(data.token));
        dispatch(
          setInquiryDetails({
            amount: data.data.paymentLinkHistory.amount,
            orderId: data.data.paymentLinkHistory.reff_no,
            merchantName: data.data.paymentLink?.account?.merchant?.name,
            expired_at: data.data.paymentLinkHistory.expired_at,
            selected_method: data.data.paymentLinkHistory.payment_method_name,
            paymentMethod: data.data.paymentMethod,
            paymentLink: data.data.paymentLink,
            paymentLinkHistory: data.data.paymentLinkHistory,
          }),
        );

        setHistoryId(data.data.paymentLinkHistory.id);
      } catch (error) {
        console.error("Error fetching payment data:", error);
        navigate("/wrong-page");
      } finally {
        setLoading(false);
      }
    },
    [navigate, dispatch],
  );

  // ✅ Pastikan `useEffect` tetap dipanggil setiap render
  useEffect(() => {
    // Jika ada state dari navigate, gunakan itu dan update URL jika belum ada reffNo di URL
    if (location.state?.result) {
      const reffNoFromState = location.state.result.data.paymentLinkHistory.reff_no;

      // Update URL jika belum ada reffNo di URL/ tidak perlu sudah ada di second form
      // if (!reffNo) {
      //   navigate(`/instruction/${reffNoFromState}`, {
      //     replace: true,
      //     state: location.state,
      //   });
      // }

      // Set data dari state
      setPaymentData({
        amount: location.state.amount,
        orderId: location.state.orderId,
        result: location.state.result,
      });
      dispatch(setPaymentDataAction(location.state.result.data.paymentLink));
      dispatch(
        setInquiryDetails({
          amount: location.state.result.data.paymentLinkHistory.amount,
          orderId: location.state.result.data.paymentLinkHistory.reff_no,
          merchantName: location.state.result.data.paymentLink?.account?.merchant?.name,
          expired_at: location.state.result.data.paymentLinkHistory.expired_at,
          selected_method: location.state.result.data.paymentLinkHistory.payment_method_name,
          paymentMethod: location.state.result.data.paymentMethod,
          paymentLink: location.state.result.data.paymentLink,
          paymentLinkHistory: location.state.result.data.paymentLinkHistory,
        }),
      );
      setHistoryId(location.state.result.data.paymentLinkHistory.id);
    }
    // Jika tidak ada state tapi ada reffNo di URL, fetch dari API
    else if (reffNo) {
      fetchPaymentData(reffNo);
    }
    // Jika tidak ada keduanya, redirect ke error page
    else {
      navigate("/wrong-page");
    }
  }, [location.state, reffNo, navigate, fetchPaymentData, dispatch]);

  // ✅ Hooks `useCallback` tetap dipanggil di tempat yang benar
  // `silent` suppresses the "still pending" info toast (used by the background poll, which
  // ticks every few seconds and would otherwise spam a toast each time). `stopPolling` lets a
  // poll invocation tear down its own interval once a terminal/session-expired state is hit —
  // the manual "Check Payment Status" button call omits both and keeps its original behavior.
  const checkTransactionStatus = useCallback(async (options = {}) => {
    // Layout / click handlers may pass a reff string or a click event; only a
    // plain options object should control silent polling.
    const { silent = false, stopPolling } =
      options && typeof options === "object" && !Array.isArray(options) && typeof options.preventDefault !== "function"
        ? options
        : {};

    if (!historyId) return null;

    const checkReffNo = paymentData?.result?.data?.paymentLinkHistory?.reff_no || paymentData?.orderId;

    try {
      const response = await authorizedFetch(`${BASE_URL}/api/payment-link/check-status`, {
        dispatch,
        token: paymentLinkToken,
        refresh: { kind: "history", reffNo: checkReffNo },
        method: "POST",
        body: JSON.stringify({
          history_id: historyId,
          reff_no: checkReffNo, // Required by middleware
        }),
      });

      const data = await response.json();
      const history_check = data.transaction;

      if (history_check.status === "paid") {
        stopPolling?.();
        dispatch(
          setNotification({
            type: "success",
            title: t("thirdForm.statusNotifTitle"),
            message: t("thirdForm.paymentReceivedMessage"),
          }),
        );

        setTimeout(() => {
          const successRedirectUrl =
            history_check.success_redirect_url || getSuccessRedirectUrl(paymentData?.result?.data?.paymentLink);

          if (redirectToMerchantUrl(successRedirectUrl)) {
            return;
          }

          const paramData = {
            reffNo: history_check.reff_no || "-",
            amount: history_check.amount || "-",
            customerName: history_check.customer_name || null,
            email: history_check.customer_email || null,
            paymentMethod: history_check.payment_method || "-",
            paymentDate: history_check.payment_date || "-",
            redirectUrl: successRedirectUrl,
          };
          navigate("/payment-success", {
            replace: true,
            state: paramData,
          });
        }, 3000);
      } else if (history_check.status === "expired") {
        stopPolling?.();
        dispatch(
          setNotification({
            type: "warning",
            title: t("thirdForm.statusNotifTitle"),
            message: `${t("thirdForm.statusIsPrefix")} ${history_check.status}`,
          }),
        );

        setTimeout(() => {
          const expiredRedirectUrl =
            history_check.expired_redirect_url || getExpiredRedirectUrl(paymentData?.result?.data?.paymentLink);

          if (redirectToMerchantUrl(expiredRedirectUrl)) {
            return;
          }

          navigate("/payment-expired", {
            replace: true,
            state: {
              selectedMethod: history_check.payment_method || "-",
              reffNo: history_check.reff_no || "-",
              amount: history_check.amount || "-",
              redirectUrl: expiredRedirectUrl,
            },
          });
        }, 3000);
      } else if (history_check.status === "failed") {
        stopPolling?.();
        if (!silent) {
          dispatch(
            setNotification({
              type: "error",
              title: t("thirdForm.statusNotifTitle"),
              message: t("thirdForm.paymentFailedMessage"),
            }),
          );
        }
      } else if (!silent) {
        dispatch(
          setNotification({
            type: "info",
            title: t("thirdForm.statusNotifTitle"),
            message: `${t("thirdForm.statusIsPrefix")} ${history_check.status}`,
          }),
        );
      }

      return history_check?.status ?? null;
    } catch (error) {
      console.error("Error checking transaction status:", error);

      if (error instanceof SessionExpiredError) {
        stopPolling?.();
        dispatch(
          setNotification({
            type: "error",
            title: getErrorTitle(PaymentLinkErrorCode.SESSION_LOST),
            message: getErrorMessage(PaymentLinkErrorCode.SESSION_LOST),
          }),
        );
        return null;
      }

      if (silent) return null;

      dispatch(
        setNotification({
          type: "error",
          title: t("thirdForm.errorTitle"),
          message: t("thirdForm.errorCheckStatus"),
        }),
      );
      return null;
    }
  }, [historyId, paymentData, paymentLinkToken, dispatch, navigate, t]);

  // ✅ useEffect tetap dipanggil setiap render
  useEffect(() => {
    if (!historyId) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      const status = payload?.data?.status;
      if (status === "paid" || status === "expired") {
        checkTransactionStatus();
      } else {
        dispatch(
          setNotification({
            type: "info",
            title: t("thirdForm.paymentNotifTitle"),
            message: payload?.notification?.body,
          }),
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [historyId, dispatch, checkTransactionStatus, t]);

  // Fallback for customers who never granted (or were never asked for) notification
  // permission — the onMessage listener above will simply never fire for them, so this
  // polls check-status directly instead. Card is skipped: that flow completes synchronously
  // via its own submit response, it was never dependent on push in the first place.
  useEffect(() => {
    if (!historyId) return;
    if (areNotificationsGranted()) return;
    if (paymentData?.result?.data?.paymentMethod?.group === "card") return;

    let stopped = false;
    const timerId = setInterval(() => {
      if (stopped) return;
      checkTransactionStatus({
        silent: true,
        stopPolling: () => {
          stopped = true;
          clearInterval(timerId);
        },
      });
    }, STATUS_POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(timerId);
    };
  }, [historyId, paymentData, checkTransactionStatus]);

  // ✅ Pastikan return hanya terjadi di bawah semua hooks
  if (loading) {
    return <div className="text-center p-4">{t("thirdForm.loading")}</div>;
  }

  if (!paymentData) {
    return null;
  }

  const { amount, orderId, result } = paymentData;

  const renderPaymentInstruction = () => {
    // Calculate earliest expiration between PaymentLink and PaymentLinkHistory
    const paymentLinkExpiredAt = result.data.paymentLink.expired_at;
    const paymentLinkHistoryExpiredAt = result.data.paymentLinkHistory.expired_at;
    const earliestExpiration = getEarliestExpiration(paymentLinkExpiredAt, paymentLinkHistoryExpiredAt);

    if (process.env.REACT_APP_DEBUG) {
      console.log("Expiration Info:", {
        paymentLinkExpiredAt,
        paymentLinkHistoryExpiredAt,
        earliestExpiration,
        source:
          earliestExpiration === paymentLinkExpiredAt
            ? "PaymentLink"
            : earliestExpiration === paymentLinkHistoryExpiredAt
              ? "PaymentLinkHistory"
              : "Default",
      });
    }

    const instructionProps = {
      amount,
      orderId,
      merchantName: result.data.paymentLink.account.merchant.name,
      expired_at: earliestExpiration, // Use earliest expiration instead of just PLH expired_at
      selected_method: result.data.paymentLinkHistory.payment_method_name,
      onCheckStatus: checkTransactionStatus, // Kirim fungsi ke layout
    };

    switch (result.data.paymentMethod.group) {
      case "offline_store":
        return (
          <PaymentInstructionOffline
            {...instructionProps}
            paymentCode={result.data.paymentLinkHistory.payment_method_value}
            storeName={result.data.paymentLinkHistory.payment_method_name}
            storeCode={result.data.paymentMethod.code}
            onBack={handleGoBack}
          />
        );
      case "va":
        return (
          <PaymentInstructionVA
            {...instructionProps}
            merchantName={
              result.data.paymentMethod.code === "VA_BCA"
                ? result.data.paymentLinkHistory.payment_method_holder_name || instructionProps.merchantName
                : instructionProps.merchantName
            }
            virtualAccount={result.data.paymentLinkHistory.payment_method_value}
            bankName={result.data.paymentLinkHistory.payment_method_name}
            bankCode={result.data.paymentMethod.code}
            onBack={handleGoBack}
          />
        );
      case "qris":
        return (
          <PaymentInstructionQRIS
            {...instructionProps}
            qrString={result.data.paymentLinkHistory.payment_method_additional}
          />
        );
      case "ewallet": {
        let ewalletData = null;
        try {
          ewalletData = JSON.parse(result.data.paymentLinkHistory.payment_method_additional || "{}");
        } catch (e) {
          console.error("Failed to parse payment_method_additional:", e);
        }

        const waitingRedirect = resolveEwalletInstruction(result, ewalletData, orderId, amount);
        if (waitingRedirect?.shouldRedirectToWaiting) {
          navigate(waitingRedirect.route, { replace: true, state: waitingRedirect.state });
          return null;
        }

        const ewalletCode = result.data.paymentMethod.code;
        const ewalletUrl = ewalletData?.web_redirect_url || null;

        return (
          <PaymentInstructionEwallet
            {...instructionProps}
            ewalletName={result.data.paymentLinkHistory.payment_method_name}
            ewalletCode={ewalletCode}
            ewalletUrl={ewalletUrl}
            onBack={handleGoBack}
          />
        );
      }
      case "card": {
        let cardAdditional = {};
        try {
          cardAdditional = JSON.parse(result.data.paymentLinkHistory.payment_method_additional || "{}");
        } catch (e) {
          cardAdditional = {};
        }

        if (cardAdditional.checkout_mode === "hosted") {
          if (cardAdditional.checkout_id) {
            return (
              <PaymentInstructionCard
                {...instructionProps}
                historyId={historyId}
                bankName={result.data.paymentLinkHistory.payment_method_name}
                bankCode={result.data.paymentMethod.code}
                initialAwaitingWebhook
              />
            );
          }

          return (
            <HostedCardCheckout
              {...instructionProps}
              onCheckStatus={checkTransactionStatus}
            />
          );
        }

        return (
          <PaymentInstructionCard
            {...instructionProps}
            historyId={historyId}
            bankName={result.data.paymentLinkHistory.payment_method_name}
            bankCode={result.data.paymentMethod.code}
            initialAwaitingWebhook={Boolean(location.state?.awaitingCardWebhook)}
          />
        );
      }
      default:
        return <p>{t("thirdForm.unknownMethod")}</p>;
    }
  };

  return <div>{renderPaymentInstruction()}</div>;
};

export default ThirdForm;
